import { useState, useCallback } from 'react'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import { Formik } from 'formik'
import { string as YupString, object as YupObject } from 'yup'

import { Modal, useShowMessage } from '@pepper/ui'

import { updateCustomerDetails } from '@pepper/utils/src/api/customer-management/customer-management'

import { SelectField, TextField } from 'components/FormFields/FormFields'
import { PEPPER_ENTITIES } from 'utils/constants/organizations'

import styles from './UpdateCustomerModal.module.css'

type Props = {
  accountId: string
  organizationName?: string
  refetchOrganizationsList: () => void
}

type FormValues = {
  vendor_id: string
  pepper_organization: string
}

const formInitialValues = {
  vendor_id: '',
  pepper_organization: '',
}

const UpdateCustomerModal = NiceModal.create<Props>(
  ({ accountId, organizationName, refetchOrganizationsList }) => {
    const { visible: isModalVisible, remove: removeModal } = useModal()
    const showMessage = useShowMessage()
    const [isSubmitting, setIsSubmitting] = useState(false)

    const handleSubmit = useCallback(
      async (values: FormValues) => {
        const { vendor_id, pepper_organization } = values
        setIsSubmitting(true)
        const response = await updateCustomerDetails({
          vendorId: vendor_id,
          accountId,
          pepperOrganization: pepper_organization,
        })
        setIsSubmitting(false)
        if (response.status === 'ok') {
          refetchOrganizationsList()
          showMessage({
            type: 'success',
            message: `Customer details updated successfully.`,
          })
        } else {
          showMessage({
            type: 'error',
            message: response.error || 'Something went wrong',
          })
        }
      },
      [accountId, refetchOrganizationsList, showMessage]
    )

    return (
      <Formik<FormValues>
        initialValues={formInitialValues}
        onSubmit={async values => {
          await handleSubmit(values)
          removeModal()
        }}
        enableReinitialize={true}
        validationSchema={YupObject({
          vendor_id: YupString().trim().required('Please enter Zoho ID.'),
          pepper_organization: YupString()
            .trim()
            .required('Please select Billing Entity.'),
        })}
      >
        {(props): React.ReactNode => {
          return (
            <Modal
              title={organizationName}
              visible={isModalVisible}
              closable={true}
              centered={true}
              className={styles.updateCustomerModal}
              onOk={props.submitForm}
              onCancel={() => {
                removeModal()
              }}
              okButtonProps={{
                disabled: isSubmitting,
              }}
              okText="Update"
            >
              <SelectField
                name="pepper_organization"
                label="Select Billing Entity"
                setFieldValue={props.setFieldValue}
                setFieldTouched={props.setFieldTouched}
                required={true}
                fieldProps={{
                  options: PEPPER_ENTITIES,
                }}
              />
              <TextField
                name="vendor_id"
                label="Zoho ID"
                setFieldTouched={props.setFieldTouched}
                required={true}
                fieldProps={{}}
              />
            </Modal>
          )
        }}
      </Formik>
    )
  }
)

export default UpdateCustomerModal
